import { apiFetch } from '../api/client';
import { ApiError } from '../api/problem';
import { parseTenantEndpoint, type TenantEndpoint } from './tenantEndpoint';

/**
 * Step one of sign-in: turn a company code into a company and an API address.
 *
 * This is the one call that must go to this app's own origin rather than to the tenant's API,
 * because its answer is *what the tenant's API is*. `discovery: true` on the client says so
 * explicitly rather than relying on it happening to run before any base URL is configured.
 */
export async function resolveTenant(code: string, signal?: AbortSignal): Promise<TenantEndpoint> {
  const response = await apiFetch<unknown>(
    `/api/tenant-endpoint/${encodeURIComponent(code.trim())}`,
    { anonymous: true, discovery: true, ...(signal ? { signal } : {}) },
  );

  const endpoint = parseTenantEndpoint(response);

  if (endpoint === null) {
    // A 200 whose body is not a company is not something to proceed past. The next thing this
    // flow does is post a password, so an unreadable answer has to stop it rather than be
    // defaulted around.
    throw new ApiError(200, 'The server returned an unrecognised response for that company code.');
  }

  return endpoint;
}

/**
 * How long the caller must wait, in whole minutes, when the lookup has been throttled — or null
 * when this failure is something else.
 *
 * Reads the server's `Retry-After` via the error's status rather than re-deriving a policy the
 * server owns. The message the API sends already states the wait; this exists so the screen can
 * *behave* differently too — disabling the field it would be pointless to keep typing into.
 */
export function throttledFor(error: unknown): boolean {
  return error instanceof ApiError && error.status === 429;
}

/** Whether a failure was "no such company", as opposed to a network or server problem. */
export function isUnknownCompany(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}
